import type { RemoteData } from '@passionware/monads';
import { maybe, rd } from '@passionware/monads';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import {
  CustomFieldValueRow,
  resolveCustomFieldValue
} from 'v5/core/custom-fields/resolveCustomFieldValue';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';

/**
 * Browsing-scope CF value lens from app scope.
 *
 * - pending/error → not ready (do not treat as global)
 * - success `null` → org / global lens (intentional)
 * - success `number` → workspace lens
 *
 * Value lens follows the URL — not entity membership / enterprise_workspace_id.
 */
export function useBrowsingCustomFieldWorkspaceId(
  services: WithServices<[WithAppScopeService]>['services']
): RemoteData<number | null> {
  const scopeRd = services.appScopeService.useResolvedAppScope();
  return rd.map(scopeRd, scope =>
    maybe.getOrNull(
      appScopeUtils.getWorkspaceId(appScopeUtils.identityFromResolved(scope))
    )
  );
}

/**
 * Resolver bound to the browsing lens once app scope is ready.
 * Pending/error RemoteData means callers must not resolve yet.
 */
export function useResolveCustomFieldValue(
  services: WithServices<[WithAppScopeService]>['services']
): RemoteData<
  <T extends CustomFieldValueRow>(
    rows: T[] | null | undefined,
    definitionId: number
  ) => T | null
> {
  const workspaceIdRd = useBrowsingCustomFieldWorkspaceId(services);
  return rd.map(
    workspaceIdRd,
    workspaceId => (rows, definitionId) =>
      resolveCustomFieldValue(rows, definitionId, workspaceId)
  );
}
